/**
 * Firebase Configuration and Initialization
 * 
 * This file initializes Firebase and provides messaging functionality
 * for Firebase Cloud Messaging (FCM) push notifications.
 */

import { initializeApp, getApps, FirebaseApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, Messaging, MessagePayload } from 'firebase/messaging';

/**
 * Firebase configuration from environment variables.
 * In the browser, reads from window._ENV_ first (runtime, from /env-config.js) so FCM works when
 * env vars are injected at runtime (e.g. Kubernetes).
 */
function getFirebaseEnv(key: string): string {
  if (typeof window !== 'undefined' && window._ENV_ && typeof window._ENV_[key] === 'string') {
    return window._ENV_[key];
  }
  const v = process.env[key];
  return typeof v === 'string' ? v : '';
}

export const getFirebaseConfig = () => {
  const config = {
    apiKey: getFirebaseEnv('NEXT_PUBLIC_FIREBASE_API_KEY'),
    authDomain: getFirebaseEnv('NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN'),
    projectId: getFirebaseEnv('NEXT_PUBLIC_FIREBASE_PROJECT_ID'),
    storageBucket: getFirebaseEnv('NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET'),
    messagingSenderId: getFirebaseEnv('NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID'),
    appId: getFirebaseEnv('NEXT_PUBLIC_FIREBASE_APP_ID'),
  };

  return config;
};

/**
 * Initialize Firebase App
 * Returns existing app if already initialized
 */
export const initializeFirebase = (): FirebaseApp | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const config = getFirebaseConfig();

    // Check if Firebase is already initialized
    const apps = getApps();
    if (apps.length > 0) {
      return apps[0];
    }

    // Initialize if config is available
    if (config.apiKey && config.projectId) {
      return initializeApp(config);
    }

    return null;
  } catch (error) {
    console.error('[FCM] Error initializing Firebase:', error);
    return null;
  }
};

/**
 * Initialize Firebase Cloud Messaging
 * Returns messaging instance if available
 */
export const initializeMessaging = (): Messaging | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  // Check if messaging is already initialized
  try {
    const app = initializeFirebase();
    if (!app) {
      return null;
    }

    // Check if browser supports notifications
    if (!('Notification' in window)) {
      console.warn('[FCM] Notifications are not supported in this browser.');
      return null;
    }

    // Check if service worker is supported
    if (!('serviceWorker' in navigator)) {
      console.warn('[FCM] Service Worker is not supported in this browser.');
      return null;
    }

    return getMessaging(app);
  } catch (error) {
    console.error('[FCM] Error initializing Firebase Messaging:', error);
    return null;
  }
};

/**
 * Get FCM registration token
 * Requires VAPID key from Firebase Console
 */
function getVapidKey(): string {
  const raw =
    (typeof window !== 'undefined' && window._ENV_?.NEXT_PUBLIC_FIREBASE_VAPID_KEY) ||
    process.env.NEXT_PUBLIC_FIREBASE_VAPID_KEY ||
    '';
  return typeof raw === 'string' ? raw.trim() : '';
}

export const getFCMToken = async (messaging: Messaging): Promise<string | null> => {
  try {
    const vapidKey = getVapidKey();

    if (!vapidKey) {
      console.error('[FCM] VAPID key is not configured. Get it from Firebase Console > Project Settings > Cloud Messaging');
      return null;
    }

    const token = await getToken(messaging, { vapidKey });
    
    if (token) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[FCM] Registration token obtained:', token.substring(0, 20) + '...');
      }
      return token;
    } else {
      console.warn('[FCM] No registration token available. Request permission to generate one.');
      return null;
    }
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (message.includes('ECDSA') || message.includes('VAPID') || message.includes('public key')) {
      console.error('[FCM] Invalid VAPID key. In Firebase Console go to Project settings > Cloud Messaging > Web configuration and copy the "Key pair" (Web Push certificate). Paste it in .env.local as NEXT_PUBLIC_FIREBASE_VAPID_KEY with no spaces or quotes.');
    } else {
      console.error('[FCM] An error occurred while retrieving token:', error);
    }
    return null;
  }
};

/**
 * Setup foreground message handler
 * Shows notifications when app is in foreground
 */
export const setupForegroundMessageHandler = (
  messaging: Messaging,
  onMessageReceived?: (payload: MessagePayload) => void
): void => {
  try {
    onMessage(messaging, (payload) => {
      if (process.env.NODE_ENV === 'development') {
        console.log('[FCM] Message received in foreground:', payload);
      }
      
      // Call custom handler if provided
      if (onMessageReceived) {
        onMessageReceived(payload);
      }

      // Show browser notification when permission granted (support both notification payload and data-only)
      if (Notification.permission === 'granted') {
        const title = payload.notification?.title ?? (payload.data as { title?: string } | undefined)?.title ?? 'New Notification';
        const body = payload.notification?.body ?? (payload.data as { body?: string; message?: string } | undefined)?.body ?? (payload.data as { message?: string } | undefined)?.message ?? '';
        const icon = payload.notification?.icon ?? '/favicon.ico';

        const notification = new Notification(title, {
          body,
          icon,
          badge: '/favicon.ico',
          tag: payload.messageId || `fcm-${Date.now()}`,
          data: {
            url: (payload.data as { link?: string } | undefined)?.link || '/',
            notificationId: (payload.data as { notification_id?: string } | undefined)?.notification_id || payload.messageId,
          },
        });

        // Handle notification click
        notification.onclick = (event) => {
          event.preventDefault();
          notification.close();
          const link = (payload.data as { link?: string } | undefined)?.link;
          if (link) {
            window.open(link, '_blank');
          } else {
            window.focus();
          }
        };
      }
    });
  } catch (error) {
    console.error('[FCM] Error setting up foreground message handler:', error);
  }
};

/**
 * Get unique device ID
 * Stores in localStorage for persistence
 */
export const getDeviceId = (): string => {
  if (typeof window === 'undefined') {
    return 'server';
  }

  let deviceId = localStorage.getItem('fcm_device_id');
  if (!deviceId) {
    deviceId = 'web_' + Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
    localStorage.setItem('fcm_device_id', deviceId);
  }
  return deviceId;
};

/**
 * Check if FCM is properly configured
 */
export const isFCMConfigured = (): boolean => {
  const config = getFirebaseConfig();
  const vapidKey = getVapidKey();
  return !!(config.apiKey && config.projectId && vapidKey);
};


